import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import type {
    StockInNotePrintCompany,
    StockInNotePrintData,
} from '@/lib/stock-in-note-print'
import { useInvoiceSettings } from '@/hooks/use-invoice-query'
import { loadCompanyLogoDataUrl } from '@/lib/company-logo'
import { m } from '@/lib/paraglide/messages'
import { printReceiptHtml } from '@/lib/print-receipt'
import { buildStockInNotePrintHTML } from '@/lib/stock-in-note-print'

/** In phiếu nhập kho khổ A4 qua iframe ẩn (cùng cơ chế hóa đơn POS và tem lô).
 *  Hook lo phần thông tin hợp tác xã + logo; nơi gọi chỉ truyền nội dung phiếu. */
export function useStockInNotePrint() {
    const { data: settings } = useInvoiceSettings()
    const [logoDataUrl, setLogoDataUrl] = useState<string | undefined>(
        undefined,
    )

    useEffect(() => {
        let cancelled = false
        void loadCompanyLogoDataUrl().then((dataUrl) => {
            // Không có logo thì phiếu vẫn in được, chỉ khuyết ảnh.
            if (!cancelled && dataUrl) setLogoDataUrl(dataUrl)
        })
        return () => {
            cancelled = true
        }
    }, [])

    const company = useMemo<StockInNotePrintCompany | undefined>(() => {
        if (!settings) return undefined
        return {
            name: settings.company_name,
            taxCode: settings.company_tax_code,
            address: settings.company_address,
            phone: settings.company_phone,
            logoDataUrl,
        }
    }, [settings, logoDataUrl])

    const print = useCallback(
        (data: Omit<StockInNotePrintData, 'company'>) => {
            const html = buildStockInNotePrintHTML({ ...data, company })
            if (!printReceiptHtml(html)) {
                toast.error(m.sinp_print_failed())
            }
        },
        [company],
    )

    return { print }
}
